import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import clsx from "clsx";
import { getAllProjects } from "@/lib/markdown";
import { PROJECT_ERAS } from "@/data/projectEras";
import type { Project } from "@/types";

const ProjectDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [project, setProject] = useState<Project | null | undefined>(undefined);

  useEffect(() => {
    getAllProjects().then((projects) =>
      setProject(projects.find((p) => p.slug === slug) ?? null),
    );
  }, [slug]);

  if (project === undefined) {
    return <div className="font-mono text-sm">Loading project...</div>;
  }

  if (project === null) {
    return (
      <div className="max-w-2xl font-mono">
        <h1 className="text-2xl sm:text-3xl font-bold mb-6">
          Project Not Found
        </h1>
        <Link to="/projects" className="button-minimal">
          ← Back to the fork
        </Link>
      </div>
    );
  }

  const { label, when, dark } = PROJECT_ERAS[project.era];

  return (
    <article className="max-w-2xl font-mono">
      <Link
        to={`/projects?road=${project.era}`}
        className="text-xs link-minimal -ml-1"
      >
        ← {label} projects
      </Link>

      <header
        className={clsx(
          "fork-reveal mt-6 border-2 border-[#111827] p-6 sm:p-8",
          dark ? "bg-[#111827] text-white" : "bg-white text-[#111827]",
        )}
      >
        <div className="flex items-center justify-between gap-4 text-xs uppercase tracking-wide opacity-60">
          <span>
            {label} · {when}
          </span>
          <span>{project.status === "live" ? "Live" : "Archived"}</span>
        </div>

        <div className="mt-5 flex items-center gap-4">
          {project.logo && (
            <img
              src={project.logo}
              alt=""
              width={56}
              height={56}
              className="size-14 shrink-0 border border-current object-cover bg-white"
            />
          )}
          <h1 className="text-2xl sm:text-3xl font-bold">{project.title}</h1>
        </div>

        <p className="mt-4 text-sm opacity-80">{project.summary}</p>
        <p className="mt-3 text-xs opacity-60">{project.tech}</p>

        {project.url ? (
          <a
            href={project.url}
            target="_blank"
            rel="noopener noreferrer"
            className={clsx(
              "inline-block mt-6 border border-current px-4 py-2 text-sm transition-colors duration-200",
              dark
                ? "hover:bg-white hover:text-[#111827]"
                : "hover:bg-[#111827] hover:text-white",
            )}
          >
            Visit {new URL(project.url).hostname.replace(/^www\./, "")} ↗
          </a>
        ) : (
          <p className="mt-6 text-sm opacity-60">No longer online.</p>
        )}
      </header>

      <div
        // The typography plugin isn't loaded (Tailwind v4 needs @plugin), so give
        // the rendered markdown its own rhythm: paragraphs, headings and lists.
        className="prose mt-8 text-sm leading-relaxed [&>*+*]:mt-4 [&_h2]:mt-10 [&_h2]:text-base [&_h2]:uppercase [&_h2]:tracking-wide [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li+li]:mt-1.5"
        dangerouslySetInnerHTML={{ __html: project.content }}
      />
    </article>
  );
};

export default ProjectDetail;
